import React from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, 
  Search, 
  Video, 
  MessageSquare, 
  Mail, 
  Share2, 
  MapPin, 
  Edit3,
  Star,
  BarChart3,
  Code,
  Settings
} from 'lucide-react';

const Services = () => {
  const aiServices = [
    {
      icon: Code,
      title: 'Custom AI Software Development',
      description: 'Proprietary software solutions tailored to your business needs, from compliance tools to lead management systems.',
      features: ['FTC Compliance Software', 'Lead Management Systems', 'Quote Generators', 'Reddit Marketing Automation'],
      premium: true
    },
    {
      icon: Bot,
      title: 'AI Chatbots',
      description: 'Intelligent customer support that works 24/7, handles inquiries, and converts visitors into customers.',
      features: ['24/7 Customer Support', 'Lead Qualification', 'Appointment Booking']
    },
    {
      icon: Mail,
      title: 'Email Automation',
      description: 'Smart email sequences that nurture leads, retain customers, and drive repeat business automatically.',
      features: ['Drip Campaigns', 'Behavioral Triggers', 'A/B Testing']
    },
    {
      icon: Settings,
      title: 'Internal AI Tools',
      description: 'Advanced proprietary AI tools for SEO optimization and GEO targeting that give you competitive advantages.',
      features: ['SEO AI Analysis', 'GEO Targeting Tools', 'Option Flows Reader', 'Performance Optimization']
    }
  ];

  const traditionalServices = [
    {
      icon: Search,
      title: 'SEO Optimization',
      description: 'Comprehensive search engine optimization to improve your visibility and organic traffic.',
      highlight: 'Core Service'
    },
    {
      icon: MapPin,
      title: 'Local SEO',
      description: 'Dominate local search results and attract customers in your area.',
      highlight: 'Specialty'
    },
    {
      icon: Video,
      title: 'Video Production',
      description: 'Professional video content that engages your audience and drives conversions through our partner Arroyo Prod.',
      highlight: 'Premium',
      link: 'https://arroyoprod.com/',
      linkText: 'Visit Arroyo Prod'
    },
    {
      icon: Video,
      title: 'Live Streaming Events',
      description: 'Professional live streaming for sports events and corporate events through our partner Arroyo Prod.',
      highlight: 'Partnership',
      link: 'https://arroyoprod.com/',
      linkText: 'Visit Arroyo Prod'
    },
    {
      icon: Edit3,
      title: 'Blog Writing',
      description: 'Expert content creation that establishes authority and improves search rankings.',
      highlight: 'Content'
    },
    {
      icon: Star,
      title: 'Reputation Management',
      description: 'Protect and enhance your online reputation across all platforms.',
      highlight: 'Protection'
    },
    {
      icon: BarChart3,
      title: 'GMB Management',
      description: 'Optimize your Google My Business profile for maximum local visibility.',
      highlight: 'Local Focus'
    }
  ];

  return (
    <section id="services" className="section-padding bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-primary-100 rounded-full px-4 py-2 mb-6"
          >
            <Bot className="text-primary-600" size={16} />
            <span className="text-primary-700 text-sm font-medium">AI-Powered Solutions</span>
          </motion.div>
          
          <h2 className="text-4xl lg:text-5xl font-black text-neutral-900 mb-6">
            AI Automation & Custom Software
            <span className="text-gradient block">Development Services</span>
          </h2>
          
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Local Finder LLC combines cutting-edge AI automation with proven marketing strategies 
            to transform local business operations and accelerate growth in Pasadena, CA and nationwide.
          </p>
        </motion.div>

        {/* AI Services Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-neutral-900 mb-4">AI Automation Services</h3>
            <p className="text-lg text-neutral-600">Revolutionary AI solutions that work while you sleep</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aiServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className={`${service.premium ? 'bg-gradient-to-br from-primary-50 to-accent-50 border-2 border-primary-200' : 'bg-white border border-neutral-200'} rounded-2xl p-8 shadow-lg hover:shadow-glow transition-all duration-300 relative overflow-hidden`}
              >
                {service.premium && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-primary-600 to-accent-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      PREMIUM
                    </span>
                  </div>
                )}
                
                <div className={`w-16 h-16 ${service.premium ? 'bg-gradient-to-br from-primary-600 to-accent-600' : 'bg-gradient-to-br from-primary-500 to-accent-500'} rounded-xl flex items-center justify-center mb-6`}>
                  <service.icon className="text-white" size={32} />
                </div>
                
                <h4 className="text-xl font-bold text-neutral-900 mb-3">{service.title}</h4>
                <p className="text-neutral-600 mb-6">{service.description}</p>
                
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-sm text-neutral-700">
                      <div className={`w-1.5 h-1.5 ${service.premium ? 'bg-accent-500' : 'bg-primary-500'} rounded-full mr-3`}></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Traditional Services Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-neutral-900 mb-4">Proven Marketing Services</h3>
            <p className="text-lg text-neutral-600">Time-tested strategies that deliver consistent results</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {traditionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-neutral-200 overflow-hidden"
              >
                {/* Highlight Badge */}
                <div className="absolute top-4 right-4">
                  <span className={`${service.highlight === 'Partnership' ? 'bg-purple-100 text-purple-700' : 'bg-accent-100 text-accent-700'} text-xs font-semibold px-3 py-1 rounded-full`}>
                    {service.highlight}
                  </span>
                </div>

                <div className="w-14 h-14 bg-neutral-100 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="text-neutral-700" size={28} />
                </div>
                
                <h4 className="text-xl font-bold text-neutral-900 mb-3">{service.title}</h4>
                <p className="text-neutral-600 mb-4">{service.description}</p>
                
                {service.link && (
                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium text-sm transition-colors duration-300"
                  >
                    {service.linkText} →
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-3xl p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Automate Your Success?</h3>
            <p className="text-xl mb-8 opacity-90">
              Let's discuss how our AI solutions can transform your business operations
            </p>
            <motion.a
              href="#contact"
              className="inline-flex items-center space-x-2 bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-neutral-100 transition-colors duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Start Your Transformation</span>
              <Bot size={20} />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;